import { Button } from "@mui/material"
import { Form, Input, Select, Divider, Switch } from "antd"
import { getLocationData } from "appRedux/slices/location"
import { RootState } from "appRedux/store/configureStore"
import {
  agencyList,
  getLiveCallingUserData,
  getStatusByUsers,
  updateStatusByUser,
  updateTagByUser,
  uploadFile,
  upsertCalling,
} from "clientServices/liveCallingService"
import { getStaticDataList } from "clientServices/staticDataService"
import { Language, genderOptions, languageOptions } from "constants/uiConstants"
import _ from "lodash"
import React, { ChangeEvent, useEffect, useMemo, useState } from "react"
import { MdOutlineAdd } from "react-icons/md"
import { RootStateOrAny, useDispatch, useSelector } from "react-redux"
import { RouteComponentProps } from "react-router-dom"
import { Card, Row, Col, CardBody } from "reactstrap"
import PricingSection from "./PricingSection"
import toastr from "toastr"
import { capitalizeFirstLetter } from "util/common"

interface Props extends RouteComponentProps {
  item: { user_uuid: string; profile_pic: string; user_name: string }
  queryParams: { [key: string]: string | number }
}

type FormData = {
  user_uuid: string
  user_name: string
  live_call_name: string
  languages: Language[]
  location: string[]
  age: number
  verified: boolean
  is_primary: boolean
  audio_call_extend_duration_1: number
  audio_call_extend_duration_1_list_price: number
  audio_call_extend_duration_1_discounted_price: number
  audio_call_extend_duration_2: number
  audio_call_extend_duration_2_list_price: number
  audio_call_extend_duration_2_discounted_price: number
  audio_call_extend_duration_3: number
  audio_call_extend_duration_3_list_price: number
  audio_call_extend_duration_3_discounted_price: number
  audio_call_v_margin_pct: number
  audio_call_price: number
  audio_call_discount_price: number
  audio_call_multiplier: number
  dream_chat_extend_duration_1: number
  dream_chat_extend_duration_1_list_price: number
  dream_chat_extend_duration_1_discounted_price: number
  dream_chat_extend_duration_2: number
  dream_chat_extend_duration_2_list_price: number
  dream_chat_extend_duration_2_discounted_price: number
  dream_chat_extend_duration_3: number
  dream_chat_extend_duration_3_list_price: number
  dream_chat_extend_duration_3_discounted_price: number
  dream_chat_v_margin_pct: number
  dream_chat_price: number
  dream_chat_discount_price: number
  dream_chat_multiplier: number
  dream_chat_start_with_min_balance: boolean
  audio_call_start_with_min_balance: boolean
  dream_chat_ios_v_margin_pct: number
  dream_chat_android_v_margin_pct: number
  audio_call_ios_v_margin_pct: number
  audio_call_android_v_margin_pct: number
  video_call_extend_duration_1: number
  video_call_extend_duration_1_list_price: number
  video_call_extend_duration_1_discounted_price: number
  video_call_extend_duration_2: number
  video_call_extend_duration_2_list_price: number
  video_call_extend_duration_2_discounted_price: number
  video_call_extend_duration_3: number
  video_call_extend_duration_3_list_price: number
  video_call_extend_duration_3_discounted_price: number
  video_call_v_margin_pct: number
  video_call_price: number
  video_call_discount_price: number
  video_call_multiplier: number
  video_call_start_with_min_balance: boolean
  video_call_ios_v_margin_pct: number
  video_call_android_v_margin_pct: number
  audio_room_multiplier: number
  audio_room_price: number
  audio_room_discount_price: number
  audio_room_start_with_min_balance: boolean,
  rank: number
}

type CallPackage = {
  duration: number
  price: number | undefined
  discount_price: number | undefined
}
interface UserInfo {
  profile_pic: string
  user_name: string
  age: string
  gender: string
  interests: Array<string>
  location: Array<string>
  audio_call: { price: string }
  agency_id: string
  video_enabled: Boolean
  free_call_enabled: Boolean
  verified: Boolean
  created_date: string
}

type FileData = {
  [fileName: string]: File | undefined
}

type FileType = "audio" | "image" | "video"

type UserMedia = {
  type: FileType
  url: string
  is_primary?: boolean
}

type AgencList = {
  uuid: string
  name: string
  partner_type: string
}
type Error = {
  message: string
}

const unnecessaryFields = [
  "audio_call_extend_duration_1","audio_call_extend_duration_1_list_price","audio_call_extend_duration_1_discounted_price","audio_call_extend_duration_2",
  "audio_call_extend_duration_2_list_price","audio_call_extend_duration_2_discounted_price","audio_call_extend_duration_3","audio_call_extend_duration_3_list_price",
  "audio_call_extend_duration_3_discounted_price","audio_call_multiplier","audio_call_price","audio_call_v_margin_pct","audio_call_start_with_min_balance",
  "audio_call_ios_v_margin_pct","audio_call_android_v_margin_pct",
  "dream_chat_extend_duration_1","dream_chat_extend_duration_1_list_price","dream_chat_extend_duration_1_discounted_price",
  "dream_chat_extend_duration_2","dream_chat_extend_duration_2_list_price","dream_chat_extend_duration_3_discounted_price",
  "dream_chat_extend_duration_2_discounted_price","dream_chat_extend_duration_3","dream_chat_extend_duration_3_list_price",
  "dream_chat_v_margin_pct","audio_call_discount_price","dream_chat_discount_price","dream_chat_multiplier",
  "dream_chat_price","dream_chat_start_with_min_balance","dream_chat_ios_v_margin_pct","dream_chat_android_v_margin_pct",
  "video_call_extend_duration_1","video_call_extend_duration_1_list_price","video_call_extend_duration_1_discounted_price","video_call_extend_duration_2",
  "video_call_extend_duration_2_list_price","video_call_extend_duration_2_discounted_price","video_call_extend_duration_3","video_call_extend_duration_3_list_price",
  "video_call_extend_duration_3_discounted_price","video_call_multiplier","video_call_price","video_call_v_margin_pct","video_call_start_with_min_balance",
  "video_call_ios_v_margin_pct","video_call_android_v_margin_pct","video_call_discount_price","audio_room_multiplier","audio_room_start_with_min_balance",
  "audio_room_price","audio_room_v_margin_pct","audio_room_discount_price"
]

const callPackagesConfig: Array<{ durationKey: string; priceKey: string; discountKey: string }>  = [
  { durationKey: 'extend_duration_1', priceKey: 'extend_duration_1_list_price', discountKey: 'extend_duration_1_discounted_price' },
  { durationKey: 'extend_duration_2', priceKey: 'extend_duration_2_list_price', discountKey: 'extend_duration_2_discounted_price' },
  { durationKey: 'extend_duration_3', priceKey: 'extend_duration_3_list_price', discountKey: 'extend_duration_3_discounted_price' },
]

export default function ManageCreators(props: Props) {
  const [files, setFiles] = useState<FileData>({})
  const [form] = Form.useForm()
  const [agencyLists, setagencyLists] = useState<AgencList[]>([])
  const [userMedia, setuserMedia] = useState<UserMedia[]>([])
  const [userStatus, setuserStatus] = useState()
  const [userTag, setUserTag] = useState("")
  const [errors, setErrors] = useState<Error[]>([
    { message: "" },
    { message: "" },
    { message: "" },
  ])
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null)
  const [userUUid, setUserUUID] = useState(props.location.search.split("=")[1])
  const [additionalMedia, setadditionalMedia] = useState<UserMedia[]>([])
  const [flag, setFlag] = useState(true)
  const [tagOptions,setTagOptions]=useState([])
  const dispatch = useDispatch()
  const locationData = useSelector(
    (state: RootState) => state.locationReducer.locationList
  )
  const currentUser= useSelector((state:RootStateOrAny) => state.currentUser)

  const userPermissions = _.values(currentUser.currentUser.data.jotPermissions)

  const hasOnboardPermissions = userPermissions.includes("EDIT_AUDIO_CALL_ONBOARD")
  const hasStatusPermissions = userPermissions.includes("EDIT_AUDIO_CALL_STATUS")
  const hasPricingPermissions = userPermissions.includes("EDIT_AUDIO_CALL_PRICING")
  

  useEffect(() => {
    dispatch(getLocationData())
    ;(async () => {
      let res = await agencyList({ type: "AUDIO_CALL" })
      let modAgency = res?.data?.map((item: AgencList) => {
        return { label: item.name, value: item.uuid }
      })
      setagencyLists(modAgency)
    })()
  }, [])

  useEffect(() => {
    fetchStaticTag()
    if (userUUid) {
      ;(async () => {
        const userStatusData = await getStatusByUsers([userUUid])
        setuserStatus(userStatusData[userUUid]?.status || "")
        let res = await getLiveCallingUserData({ filters: { id: userUUid } })
        setUserInfo(res.data[0])
        if (res.data[0]) {

          form.setFieldsValue({
            user_uuid: res.data[0].user_uuid?.trim(),
            user_name: res.data[0].user_name,
            languages: res.data[0].languages,
            age: res.data[0].age,
            gender: res.data[0].gender,
            interests: res.data[0].interests,
            location: res.data[0].location,
            price_per_minute: res.data[0].price_per_minute,
            verified: res.data[0].verified,
            free_call_enabled: res.data[0].free_call_enabled,
            agency_id: res.data[0].agency_id,
            audio_call_multiplier: res.data[0].audio_call?.multiplier,
          audio_call_extend_duration_1:
            res.data[0].audio_call?.call_packages &&
            res.data[0].audio_call?.call_packages[0]?.duration,
          audio_call_extend_duration_1_list_price:
            res.data[0].audio_call?.call_packages &&
            res.data[0].audio_call?.call_packages[0]?.price,
          audio_call_extend_duration_1_discounted_price:
            res.data[0].audio_call?.call_packages &&
            res.data[0].audio_call?.call_packages[0]?.discount_price,
          audio_call_extend_duration_2:
            res.data[0].audio_call?.call_packages &&
            res.data[0].audio_call?.call_packages[1]?.duration,
          audio_call_extend_duration_2_list_price:
            res.data[0].audio_call?.call_packages &&
            res.data[0].audio_call?.call_packages[1]?.price,
          audio_call_extend_duration_2_discounted_price:
            res.data[0].audio_call?.call_packages &&
            res.data[0].audio_call?.call_packages[1]?.discount_price,
          audio_call_extend_duration_3:
            res.data[0].audio_call?.call_packages &&
            res.data[0].audio_call?.call_packages[2]?.duration,
          audio_call_extend_duration_3_list_price:
            res.data[0].audio_call?.call_packages &&
            res.data[0].audio_call?.call_packages[2]?.price,
          audio_call_extend_duration_3_discounted_price:
            res.data[0].audio_call?.call_packages &&
            res.data[0].audio_call?.call_packages[2]?.discount_price,
          audio_call_discount_price: res.data[0].audio_call?.discount_price,
          audio_call_price: res.data[0].audio_call?.price,
          audio_call_v_margin_pct: res.data[0].audio_call?.v_margin_pct,
          audio_call_ios_v_margin_pct: res.data[0].audio_call?.ios_v_margin_pct,
          audio_call_android_v_margin_pct :res.data[0].audio_call?.android_v_margin_pct,
          audio_call_start_with_min_balance: res.data[0].audio_call?.start_with_min_balance,
            dream_chat_multiplier: res.data[0].live_chat?.multiplier,
            dream_chat_extend_duration_1:
              res.data[0].live_chat?.call_packages &&
              res.data[0].live_chat?.call_packages[0]?.duration,
            dream_chat_extend_duration_1_list_price:
              res.data[0].live_chat?.call_packages &&
              res.data[0].live_chat?.call_packages[0]?.price,
            dream_chat_extend_duration_1_discounted_price:
              res.data[0].live_chat?.call_packages &&
              res.data[0].live_chat?.call_packages[0]?.discount_price,
            dream_chat_extend_duration_2:
              res.data[0].live_chat?.call_packages &&
              res.data[0].live_chat?.call_packages[1]?.duration,
            dream_chat_extend_duration_2_list_price:
              res.data[0].live_chat?.call_packages &&
              res.data[0].live_chat?.call_packages[1]?.price,
            dream_chat_extend_duration_2_discounted_price:
              res.data[0].live_chat?.call_packages &&
              res.data[0].live_chat?.call_packages[1]?.discount_price,
            dream_chat_extend_duration_3:
              res.data[0].live_chat?.call_packages &&
              res.data[0].live_chat?.call_packages[2]?.duration,
            dream_chat_extend_duration_3_list_price:
              res.data[0].live_chat?.call_packages &&
              res.data[0].live_chat?.call_packages[2]?.price,
            dream_chat_extend_duration_3_discounted_price:
              res.data[0].live_chat?.call_packages &&
              res.data[0].live_chat?.call_packages[2]?.discount_price,
            dream_chat_discount_price: res.data[0].live_chat?.discount_price,
            dream_chat_price: res.data[0].live_chat?.price,
            dream_chat_v_margin_pct: res.data[0].live_chat?.v_margin_pct,
            dream_chat_ios_v_margin_pct: res.data[0].live_chat?.ios_v_margin_pct,
            dream_chat_android_v_margin_pct :res.data[0].live_chat?.android_v_margin_pct,
            dream_chat_start_with_min_balance: res.data[0].live_chat?.start_with_min_balance,
              video_call_multiplier: res.data[0].video_call?.multiplier,
              video_call_extend_duration_1:
                res.data[0].video_call?.call_packages &&
                res.data[0].video_call?.call_packages[0]?.duration,
              video_call_extend_duration_1_list_price:
                res.data[0].video_call?.call_packages &&
                res.data[0].video_call?.call_packages[0]?.price,
              video_call_extend_duration_1_discounted_price:
                res.data[0].video_call?.call_packages &&
                res.data[0].video_call?.call_packages[0]?.discount_price,
              video_call_extend_duration_2:
                res.data[0].video_call?.call_packages &&
                res.data[0].video_call?.call_packages[1]?.duration,
              video_call_extend_duration_2_list_price:
                res.data[0].video_call?.call_packages &&
                res.data[0].video_call?.call_packages[1]?.price,
              video_call_extend_duration_2_discounted_price:
                res.data[0].video_call?.call_packages &&
                res.data[0].video_call?.call_packages[1]?.discount_price,
              video_call_extend_duration_3:
                res.data[0].video_call?.call_packages &&
                res.data[0].video_call?.call_packages[2]?.duration,
              video_call_extend_duration_3_list_price:
                res.data[0].video_call?.call_packages &&
                res.data[0].video_call?.call_packages[2]?.price,
              video_call_extend_duration_3_discounted_price:
                res.data[0].video_call?.call_packages &&
                res.data[0].video_call?.call_packages[2]?.discount_price,
              video_call_discount_price: res.data[0].video_call?.discount_price,
              video_call_price: res.data[0].video_call?.price,
              video_call_v_margin_pct: res.data[0].video_call?.v_margin_pct,
              video_call_ios_v_margin_pct: res.data[0].video_call?.ios_v_margin_pct,
              video_call_android_v_margin_pct :res.data[0].video_call?.android_v_margin_pct,
              video_call_start_with_min_balance: res.data[0].video_call?.start_with_min_balance,
            audio_room_multiplier: res.data[0].audio_room?.multiplier,
            audio_room_start_with_min_balance: res.data[0].audio_room?.start_with_min_balance,
            audio_room_discount_price: res.data[0].audio_room?.discount_price,
            audio_room_price: res.data[0].audio_room?.price,
            audio_room_v_margin_pct: res.data[0].audio_room?.v_margin_pct,
            audio_enabled: res.data[0].audio_enabled,
            chat_enabled: res.data[0].chat_enabled,
            audio_room_enabled: res.data[0].audio_room_enabled,
            video_enabled: res.data[0].video_enabled,
            rank: res.data[0].rank,
          })
          setUserTag(res.data[0].tag)
          setFlag(false)

          let additionaluserMedia = res.data[0].user_media?.filter(
            (item: any) => item.type == "image" && item.is_primary != true
          )
          let userMedia = []
          let userMedia2 = res.data[0].user_media?.filter(
            (item: any) =>
              item.type == "audio" ||
              item.is_primary == true ||
              item.type == "video"
          )
          userMedia.push(
            res.data[0].user_media?.filter(
              (item: any) => item.type == "audio"
            )[0]
          )
          userMedia.push(
            res.data[0].user_media?.filter(
              (item: any) => item.type == "video"
            )[0] || {}
          )
          userMedia.push(
            res.data[0].user_media?.filter(
              (item: any) => item.is_primary == true
            )[0]
          )

          setadditionalMedia(additionaluserMedia)
          setuserMedia(userMedia)
        }
      })()
    }else{
      form.setFieldsValue({
        audio_room_start_with_min_balance: true
      })
    }
  }, [])

  const cityLocationList = useMemo(() => {
    if (!locationData) return [];
    return locationData
      .map((location: { name: string; dhTagId: string }) => ({
        label: capitalizeFirstLetter(location.name),
        value: location.dhTagId,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [locationData]);
  

  const onFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    fileType: FileType
  ) => {
    const input = event.target as HTMLInputElement
    if (input.files && input.files.length > 0) {
      setFiles(prevFiles => ({
        ...prevFiles,
        [fileType]: input.files?.[0],
      }))
    }
  }

  const handleFileUpload = async (
    fileType: FileType, 
    index: number, 
    isAdditional: boolean = false
  ) => {
    try {
      const userId = form.getFieldValue("user_uuid")?.trim()
      if (!userId) {
        toastr.error("Please provide creator id to upload files!")
        return
      }
      const file = files[fileType]
      if (!file) return
      const formData = new FormData()
      formData.append("file", file)
      formData.append("userId", userId)
      const response = await uploadFile(formData)
      
      // Choose which state updater to use
      const updateState = isAdditional ? setadditionalMedia : setuserMedia
      updateState(prev => {
        const newMedia = [...prev]
        newMedia[index] = {
          url: response.azureUrl,
          type: fileType
        }
        return newMedia
      })
      toastr.success("Upload successful...")
    } catch (error) {
      toastr.clear()
    }
  }
  

  const onSubmit = async (formValues: FormData) => {
    let values = JSON.parse(JSON.stringify(formValues));

    // console.log(values)
    if (! values.audio_enabled){
      values.audio_enabled=false
    }
    if (! values.video_enabled){
      values.video_enabled=false
    }

    if (! values.chat_enabled){
      values.chat_enabled=false
    }

    if (! values.audio_room_enabled){
      values.audio_room_enabled=false
    }

    let user_media = []
    //error on media
    if (userUUid) {
      if (userMedia[2] && userMedia[2]?.url !== "") {
        userMedia[2].is_primary = true
      }
    } else {
      // if (userMedia[0] && userMedia[0]?.url !== "") {
      //   let error = [...errors]
      //   error[0].message = ""
      //   setErrors(error)
      // } else {
      //   let error = [...errors]
      //   error[0].message = "Intro file is mandatory"
      //   setErrors(error)
      //   return
      // }
      if (userMedia[2] && userMedia[2]?.url !== "") {
        let error = [...errors]
        error[2].message = ""
        setErrors(error)
      } else {
        let error = [...errors]
        error[2].message = "Primary Image is mandatory"
        setErrors(error)
        return
      }

      userMedia[2].is_primary = true
    }

    values.audio_call = {
      allowed: true,
      discount_price: +values.audio_call_discount_price,
      price: +values.audio_call_price,
      v_margin_pct: +values.audio_call_v_margin_pct,
      call_packages: [],
      multiplier: +values.audio_call_multiplier,
      start_with_min_balance: values.audio_call_start_with_min_balance ?? false,
      ...(values.audio_call_ios_v_margin_pct && { ios_v_margin_pct: +values.audio_call_ios_v_margin_pct }),
      ...(values.audio_call_android_v_margin_pct && { android_v_margin_pct: +values.audio_call_android_v_margin_pct }),
    }

    if (userUUid) {
      values.action = "update"
    }

    values.live_call_name = values.user_name

    function addCallPackages( prefix: string, targetArray: CallPackage[]): void {
      callPackagesConfig.forEach(({ durationKey, priceKey, discountKey }) => {
        if (values[prefix + durationKey] || values[prefix + priceKey] || values[prefix + discountKey]) {
          targetArray.push({
            duration: values[prefix + durationKey],
            price: values[prefix + priceKey] ? +values[prefix + priceKey] : undefined,
            discount_price: values[prefix + discountKey] ? +values[prefix + discountKey] : undefined,
          });
        }
      });
    }
  

    addCallPackages('audio_call_',values.audio_call.call_packages)

    values.video_call = {
      allowed: true,
      discount_price: +values.video_call_discount_price,
      price: +values.video_call_price,
      v_margin_pct: +values.video_call_v_margin_pct,
      call_packages: [],
      multiplier: +values.video_call_multiplier,
      start_with_min_balance: values.video_call_start_with_min_balance ?? false,
      ...(values.video_call_ios_v_margin_pct && { ios_v_margin_pct: +values.video_call_ios_v_margin_pct }),
      ...(values.video_call_android_v_margin_pct && { android_v_margin_pct: +values.video_call_android_v_margin_pct }),
    }

    addCallPackages('video_call_',values.video_call.call_packages)

    values.audio_room = {
      allowed: true,
      discount_price: +values.audio_room_discount_price,
      price: +values.audio_room_price,
      v_margin_pct: +values.audio_room_v_margin_pct,
      multiplier: +values.audio_room_multiplier,
      start_with_min_balance: values.audio_room_start_with_min_balance ?? false,
    }

    user_media = userMedia.filter(n => n)
    const additionalMediaContent=additionalMedia.filter((media) => media.url != "")
    user_media = [...user_media, ...additionalMediaContent]

    const userValues: Record<string, any> = {};
    if(userTag){
      userValues["tag"] = userTag
    }
    // if(userStatus){
    //   userValues["status"]=userStatus
    // }

    values.live_chat = {
      allowed: true,
      discount_price: +values.dream_chat_discount_price,
      price: +values.dream_chat_price,
      v_margin_pct: +values.dream_chat_v_margin_pct,
      call_packages: [],
      multiplier: +values.dream_chat_multiplier,
      start_with_min_balance: values.dream_chat_start_with_min_balance ?? false,
      ...(values.dream_chat_ios_v_margin_pct && { ios_v_margin_pct: +values.dream_chat_ios_v_margin_pct }),
      ...(values.dream_chat_android_v_margin_pct && { android_v_margin_pct: +values.dream_chat_android_v_margin_pct }),
    }

    addCallPackages('dream_chat_',values.live_chat.call_packages)

    unnecessaryFields.forEach((field) => delete values[field]);
    values.user_uuid = values.user_uuid.trim()
    values.rank = values.rank != "" && values.rank != null ? parseInt(values.rank): 100
    // console.log({ ...values, user_media: user_media, ...userValues })
    await upsertCalling({ ...values, user_media: user_media, ...userValues })
  }

  const addMore = () => {
    setadditionalMedia(prev => {
      const newAddUserMedia = [...prev]
      newAddUserMedia.push({
        type: "image",
        url: "",
      })

      return newAddUserMedia
    })
  }
  const handleToggleUpload = (e: any) => {
    if (e.target.value == "") {
      setFlag(true)
    } else {
      setFlag(false)
    }
  }
  const updateStatus = () =>{
    updateStatusByUser({user_uuid: userUUid?.trim(),status: userStatus})
  }

  const updateTag = () => {
    updateTagByUser({user_uuid: userUUid?.trim(),tag:userTag})
  }
  
  const handleInputChange = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const input = e.target as HTMLInputElement;
    const value = input.value + e.key;
  
    // // Allow only numbers without leading zeros
    if (!/^([1-9][0-9]*)$/.test(value) && e.key !== 'Backspace' && e.key !== 'Delete') {
      e.preventDefault();
    }
  }

  const handleDeleteAdditionalMedia = (index: number) => {
    setadditionalMedia(prev => {
      const newMedia = [...prev];
      newMedia.splice(index, 1);
      return newMedia;
    });
  };

  const fetchStaticTag = async () => {
    try {
      const data = await getStaticDataList({
        propertyName: "live_calling_tag",
        currentPage: 1,
        pageSize: 10,
      })
      const tagOptions = data.rows[0]?.value || []
      setTagOptions(tagOptions)
    } catch (error) {
      console.error("Error fetching static tags:", error)
    }
  }

  console.log({hasOnboardPermissions,hasStatusPermissions,hasPricingPermissions}, flag,flag ? hasStatusPermissions ? true : false : false)
  return (
    <Card>
      <CardBody>
        <Row>
          <Col md={11}></Col>
          <Col md={1}>
            <Button
              color="info"
              variant="contained"
              style={{
                // width: "40%",
                marginBottom: "10px",
              }}
              onClick={() => {
                props.history.push("/live/creators/browse")
              }}
              size="medium"
            >
              Back
            </Button>
          </Col>
        </Row>

        <br />
        <Form
          form={form}
          layout="vertical"
          autoComplete="false"
          onFinish={onSubmit}
        >
          <Row>
            <Col lg="6">
              <Form.Item
                label="Creator ID"
                name="user_uuid"
                rules={[{ required: true, message: "Creator ID is required" }]}
              >
                <Input
                  onChange={e => handleToggleUpload(e)}
                  type="text"
                  size="large"
                  disabled={userInfo ? true : false}
                />
              </Form.Item>
            </Col>
            <Col lg="6">
              <Form.Item
                label="Profile Name"
                name="user_name"
                rules={[
                  { required: true, message: "Profile Name is required" },
                ]}
              >
                <Input type="text" size="large" />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col lg="6">
              <Form.Item
                label="Languages"
                name="languages"
                rules={[
                  {
                    required: true,
                    message: "At least one language must be selected",
                  },
                ]}
              >
                <Select
                  mode="multiple"
                  size="large"
                  options={languageOptions}
                />
              </Form.Item>
            </Col>
            <Col lg="3">
              <Form.Item
                label="Age"
                name="age"
                rules={[{ required: true, message: "Age is required" }]}
              >
                <Input type="number" size="large" min="1" onKeyDown={handleInputChange}/>
              </Form.Item>
            </Col>
            <Col lg="3">
              <Form.Item
                label="Gender"
                name="gender"
                rules={[{ required: true, message: "Gender is required" }]}
              >
                <Select size="large" options={genderOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col lg="6">
              <Form.Item label="Interests" name="interests">
                <Select
                  mode="multiple"
                  size="large"
                  options={[
                    { label: "Yoga", value: "yoga" },
                    { label: "Gym", value: "gym" },
                    { label: "Fish lover", value: "fish_lover" },
                    { label: "Dog", value: "dog" },
                    { label: "Cat", value: "cat" },
                    { label: "Beach lover", value: "beach_lover" },
                    { label: "Mountain lover", value: "mountain_lover" },
                    { label: "Road trips", value: "road_trips" },
                    { label: "Casual friendship", value: "casual_friendship" },
                    {
                      label: "Casual relationship",
                      value: "casual_relationship",
                    },
                    { label: "Astrology", value: "astrology" },
                    { label: "Dancing", value: "dancing" },
                    { label: "Singing", value: "singing" },
                    { label: "Reading books", value: "reading_books" },
                    { label: "Hiking", value: "hiking" },
                    { label: "Camping", value: "camping" },
                    { label: "Cooking", value: "cooking" },
                    { label: "Beer lover", value: "beer_lover" },
                    { label: "Writing", value: "writing" },
                    { label: "Video Games", value: "video_games" },
                    { label: "Chai lover", value: "chai_lover" },
                    { label: "Coffee lover", value: "coffee_lover" },
                    { label: "Horror movies", value: "horror_movies" },
                    { label: "Romantic movies", value: "romantic_movies" },
                    { label: "Comedy movies", value: "comedy_movies" },
                    { label: "Gardening", value: "gardening" },
                    { label: "Photography", value: "photography" },
                    { label: "Shopping", value: "shopping" },
                    { label: "Football", value: "football" },
                    { label: "Cricket", value: "cricket" },
                    { label: "Hockey", value: "hockey" },
                    { label: "Swimming", value: "swimming" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col lg="3">
              <Form.Item
                label="Location"
                name="location"
                rules={[{ required: true, message: "Location is required" }]}
              >
                <Select size="large" options={cityLocationList} />
              </Form.Item>
            </Col>
            <Col lg="3">
              <Form.Item
                label="Rank"
                name="rank"
                // rules={[{ required: true, message: "Rank is required" }]}
              >
                <Input type="number" size="large" min={1} defaultValue={100} onKeyDown={handleInputChange}/>
              </Form.Item>
            </Col>
            <Col lg="3">
              <Form.Item
                label="Verified"
                name="verified"
                rules={[{ required: true, message: "This field is required" }]}
              >
                <Select
                  size="large"
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col lg="3">
              <Form.Item
                label="Eligible for free calls"
                name="free_call_enabled"
                rules={[{ required: true, message: "This field is required" }]}
              >
                <Select
                  size="large"
                  options={[
                    { label: "Yes", value: true },
                    { label: "No", value: false },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item
                label="Agency ID"
                name="agency_id"

                // rules={[{ required: true, message: "Agency ID is required" }]}
              >
                <Select
                  size="large"
                  options={agencyLists}
                  placeholder="Blank if creator is not associated with agency"
                />
              </Form.Item>
            </Col>
          </Row>
            <React.Fragment>
              <Divider style={{ borderColor: "#bbb" }}>
                Status | Audio Call
                </Divider>
              <Row>
                <Col lg="4">
                  <Select
                      style={{width: "100%"}}
                      size="large"
                      options={[
                        { label: "OFFLINE", value: "OFFLINE" },
                        { label: "ONLINE", value: "ONLINE" },
                        { label: "BUSY", value: "BUSY" },
                      ]}
                      disabled={!hasStatusPermissions}
                      value={userStatus}
                      onChange={e => {setuserStatus(e)}}
                    />
                </Col>
                <Col>
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={flag ? true : hasStatusPermissions ? false : true }
                    onClick={() => hasStatusPermissions ? updateStatus() : {} }
                  >
                    Update Status
                  </Button>
                </Col>
              </Row>
              </React.Fragment>
              <React.Fragment>
              <Row className="align-items-center mt-3">
                <Col lg="1">
                  <h5>Audio Call</h5>
                </Col>
                <Col lg="1">
                  <Form.Item
                    name="audio_enabled"
                    valuePropName="checked"
                  >
                    <Switch defaultChecked={false} disabled={!hasOnboardPermissions} />
                  </Form.Item>
                </Col>
              </Row>
              <Row className="align-items-center">
                <Col lg="1">
                  <h5>Video Call</h5>
                </Col>
                <Col lg="1">
                  <Form.Item
                    name="video_enabled"
                    valuePropName="checked"
                  >
                    <Switch defaultChecked={false} disabled={!hasOnboardPermissions} />
                  </Form.Item>
                </Col>
              </Row>
              <Row className="align-items-center">
                <Col lg="1">
                  <h5>Dream Chat</h5>
                </Col>
                <Col lg="1">
                  <Form.Item
                    name="chat_enabled"
                    valuePropName="checked"
                  >
                    <Switch defaultChecked={false} disabled={!hasOnboardPermissions} />
                  </Form.Item>
                </Col>
              </Row>
              <Row className="align-items-center">
                <Col lg="1">
                  <h5>Audio Room</h5>
                </Col>
                <Col lg="1">
                  <Form.Item
                    name="audio_room_enabled"
                    valuePropName="checked"
                  >
                    <Switch defaultChecked={false} disabled={!hasOnboardPermissions} />
                  </Form.Item>
                </Col>
              </Row>
            </React.Fragment>
            
            <React.Fragment>
              <Divider style={{ borderColor: "#bbb" }}>
                  Tags | Audio Call
              </Divider>
                <Row>
                  <Col lg="4">
                    <Select
                        style={{width: "100%"}}
                        size="large"
                        options={tagOptions}
                        value={userTag}
                        disabled={!hasStatusPermissions}
                        onChange={e => {setUserTag(e)}}
                      />
                  </Col>
                  <Col>
                    <Button
                      color="primary"
                      variant="contained"
                      disabled={flag ? true : hasStatusPermissions ? false : true }
                      onClick={() => hasStatusPermissions ? updateTag() : {} }
                    >
                      Update Tag
                    </Button>
                  </Col>
                </Row> 
            </React.Fragment>
            <PricingSection
              title="Pricing | Audio Call"
              prefix="audio_call"
              handleInputChange={handleInputChange}
              hasPricingPermissions={hasPricingPermissions}
              form={form}
            />
            <PricingSection
              title="Pricing | Dream Chat"
              prefix="dream_chat"
              handleInputChange={handleInputChange}
              hasPricingPermissions={hasPricingPermissions}
              form={form}
              audioSyncSwitch
            />
            <PricingSection
              title="Pricing | Video Call"
              prefix="video_call"
              handleInputChange={handleInputChange}
              hasPricingPermissions={hasPricingPermissions}
              form={form}
              // isMandatory={false}
              audioSyncSwitch
            />
            <PricingSection
              title="Pricing | Audio Room"
              prefix="audio_room"
              handleInputChange={handleInputChange}
              hasPricingPermissions={hasPricingPermissions}
              form={form}
              durations={false}
              mobile_margin ={false}
              // isMandatory={false}
              audioSyncSwitch
            />
          <Divider style={{ borderColor: "#bbb" }}>Media</Divider>
          <Row className="d-flex align-items-end">
            <Col md="8">
              <label className="col-form-label">Intro file (audio)</label>
              <input
                type="file"
                className="form-control"
                style={
                  errors[0].message !== "" ? { border: "1px solid red" } : {}
                }
                accept="audio/*"
                onChange={e => onFileChange(e, "audio")}
              />
              {errors[0].message !== "" ? (
                <label style={{ fontSize: "12px", color: "red" }}>
                  {errors[0].message}
                </label>
              ) : (
                <></>
              )}
            </Col>
            <Col>
              <Button
                color="primary"
                variant="contained"
                className="mx-2"
                disabled={flag}
                onClick={() => handleFileUpload("audio", 0)}
              >
                Upload
              </Button>
            </Col>

            <div>{userMedia[0]?.url && `${userMedia[0].url}`}</div>
          </Row>
          <Row className="d-flex align-items-end">
            <Col md="8">
              <label className="col-form-label">Primary Video</label>
              <input
                type="file"
                className="form-control"
                // style={errors[1]?.message !=="" ? {border:'1px solid red'}:{}}

                onChange={e => onFileChange(e, "video")}
              />
              {/* {errors[1]?.message !=="" ? <label style={{fontSize:'12px',color:'red'}}>{errors[1]?.message}</label>:<></>} */}
            </Col>
            <Col>
              <Button
                color="primary"
                variant="contained"
                className="mx-2"
                disabled={flag}
                onClick={() => handleFileUpload("video", 1)}
              >
                Upload
              </Button>
            </Col>
            {userMedia[1]?.url && (
              <Col>
                {" "}
                <video src={userMedia[1]?.url} height="50px" width="50px" />
              </Col>
            )}

            {/* <Col>
              <Form.Item label="Is Primary" name="is_primary">
                <Switch />
              </Form.Item>
            </Col> */}
            <div>{userMedia[1]?.url && `${userMedia[1].url}`}</div>
          </Row>
          <Row className="d-flex align-items-end">
            <Col md="8">
              <label className="col-form-label">Primary Image</label>
              <input
                type="file"
                className="form-control"
                style={
                  errors[2].message !== "" ? { border: "1px solid red" } : {}
                }
                accept="image/*"
                onChange={e => onFileChange(e, "image")}
              />
              {errors[2].message !== "" ? (
                <label style={{ fontSize: "12px", color: "red" }}>
                  {errors[2].message}
                </label>
              ) : (
                <></>
              )}
            </Col>
            <Col>
              <Button
                color="primary"
                variant="contained"
                className="mx-2"
                disabled={flag}
                onClick={() => handleFileUpload("image", 2)}
              >
                Upload
              </Button>
            </Col>
            {userMedia[2]?.url && (
              <Col>
                {" "}
                <img src={userMedia[2]?.url} height="50px" width="50px" />
              </Col>
            )}

            {/* <Col>
              <Form.Item label="Is Primary" name="is_primary">
                <Switch />
              </Form.Item>
            </Col> */}
            <div>{userMedia[2]?.url && `${userMedia[2].url}`}</div>
          </Row>

          <Divider style={{ borderColor: "#bbb" }}>Additional Images</Divider>
          {additionalMedia?.map((_, index) => (
            <Row className="d-flex align-items-end">
              <Col md="8">
                <label className="col-form-label">Image File</label>
                <input
                  type="file"
                  className="form-control"
                  onChange={e => onFileChange(e, "image")}
                />
              </Col>
              {_?.url && (
                <Col>
                  {" "}
                  <img src={_.url} height="50px" width="50px" />
                </Col>
              )}

              <Col>
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => handleDeleteAdditionalMedia(index)}
                >
                  Delete
                </Button>
              </Col>
              <Col>
                <Button
                  color="primary"
                  variant="contained"
                  className="mx-2"
                  disabled={flag}
                  onClick={() => handleFileUpload("image", index, true)}
                >
                  Upload
                </Button>
              </Col>
              {_?.url && `${_.url}`}
            </Row>
          ))}
          <Button
            color="info"
            variant="contained"
            startIcon={<MdOutlineAdd />}
            style={{
              marginLeft: "0px",
              marginTop: "5px",
            }}
            onClick={addMore}
          >
            Add More
          </Button>
          <Row className="d-flex justify-content-center mt-2">
            <Button
              color="success"
              variant="contained"
              style={{
                width: "98%",
              }}
              size="large"
              type="submit"
            >
              SUBMIT
            </Button>
          </Row>
        </Form>
      </CardBody>
    </Card>
  )
}
